import { Practitioner } from '@customTypes/Practitioner'
import { Choices } from '@customTypes/Choices'
import { setPreferredPractitionersAfterFetching, useChoiceDispatch, useChoiceState } from '@context/ChoiceContext'
import { getAllPractitionersByIds, getPractitioners } from 'src/services/api/practitioners'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import { useConfig } from '@context/ConfigurationContext'
import { AppMode } from '@customTypes/Generic'
import { getQueryParams } from 'src/utils/getQueryParams'
import { ParsedQuery } from 'query-string'
import { useCallback, useEffect } from 'react'
import {
  usePractitionersDispatch,
  setPractitioners as setAllPractitioners,
  usePractitionersContext,
  setPractitionersLoading,
} from '@context/PractitionersContext'
import { useInfiniteQuery } from 'react-query'
import { isEqual } from 'date-fns'
import { uniqBy } from 'lodash'
import { useGapFillerQueryParams } from './useQueryParams'

type GetParamsFunction = (
  mode: AppMode,
  choices: Choices,
  queryParams: ParsedQuery<string>
) => { treatmentBundleTypeReferenceId: string; patientType: PatientTypeOptions | undefined }

const getParams: GetParamsFunction = (mode, choices, queryParams) => {
  if (mode === AppMode.NORMAL) {
    return { treatmentBundleTypeReferenceId: choices.treatmentBundleTypeReferenceId, patientType: choices.patientType }
  }

  if (mode === AppMode.GAP_FILLER) {
    return {
      treatmentBundleTypeReferenceId: queryParams.treatmentBundleReferenceId as string,
      patientType: PatientTypeOptions.OLD,
    }
  }

  return {
    treatmentBundleTypeReferenceId: queryParams.treatmentBundleTypeReferenceId as string,
    patientType: PatientTypeOptions.OLD,
  }
}

export const usePractitioners = () => {
  const { clinicId, mode } = useConfig()
  const dispatch = useChoiceDispatch()
  const choices = useChoiceState() // this is only for regular flow, in recall flow - state is empty and we have to use query params
  const queryParams = getQueryParams()
  const { treatmentBundleTypeReferenceId, patientType } = getParams(mode, choices, queryParams)
  const practitionersDispatch = usePractitionersDispatch()
  const { availabilitiesData } = usePractitionersContext()
  const { queryParams: gapFillerQueryParams } = useGapFillerQueryParams()

  const selectPreferredPractitioners = useCallback(
    (allPractitioners: Practitioner[]) => {
      const ids = allPractitioners.map((practitioner) => practitioner.referenceId || '')
      if (mode === AppMode.NORMAL) {
        dispatch(
          setPreferredPractitionersAfterFetching({
            practitionerIds: ids,
          })
        )
      } else {
        const practitionerFromQueryParamsIsReturned =
          typeof queryParams.practitionerReferenceId === 'string' &&
          allPractitioners.some((practitioner) => practitioner.referenceId === queryParams.practitionerReferenceId)
        if (practitionerFromQueryParamsIsReturned) {
          dispatch(
            setPreferredPractitionersAfterFetching({
              practitionerIds: [queryParams.practitionerReferenceId as string],
            })
          )
        } else {
          dispatch(
            setPreferredPractitionersAfterFetching({
              practitionerIds: ids,
            })
          )
        }
      }
    },
    [dispatch, mode, queryParams.practitionerReferenceId]
  )

  const queryKey = clinicId + treatmentBundleTypeReferenceId + patientType
  const hasRequiredData = availabilitiesData.length > 0 && !!patientType && !!treatmentBundleTypeReferenceId
  const { data, fetchNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery(
    queryKey,
    async ({ pageParam }) => {
      const fromDate = pageParam?.fromDate || availabilitiesData[0]?.fromDate
      const toDate = pageParam?.toDate || availabilitiesData[0]?.toDate

      let practitioners: Practitioner[]

      if (mode === AppMode.GAP_FILLER) {
        practitioners = await getAllPractitionersByIds(clinicId, gapFillerQueryParams?.practitionerReferenceIds ?? [])
      } else {
        practitioners = await getPractitioners(
          clinicId,
          treatmentBundleTypeReferenceId,
          patientType || PatientTypeOptions.NEW,
          fromDate,
          toDate,
          choices.insuranceType
        )
      }

      return { practitioners, fromDate, toDate }
    },
    {
      getNextPageParam: ({ fromDate, toDate }) => {
        const lastAvailabilitiesIndex = availabilitiesData.findIndex(
          (availability) => isEqual(availability.fromDate, fromDate) && isEqual(availability.toDate, toDate)
        )

        const nextAvailabilities = availabilitiesData[lastAvailabilitiesIndex + 1]
        if (nextAvailabilities) {
          const nextPeriod = { fromDate: nextAvailabilities.fromDate, toDate: nextAvailabilities.toDate }

          return nextPeriod
        } else {
          return undefined
        }
      },
      enabled: hasRequiredData || mode === AppMode.GAP_FILLER,
      staleTime: 900000,
    }
  )

  const pages = data?.pages
  const pagesLength = pages?.length || 0
  const availabilitiesLength = availabilitiesData.length

  useEffect(() => {
    if (!!(pagesLength < availabilitiesLength) && !isLoading && hasRequiredData) {
      fetchNextPage()
    }
  }, [pagesLength, availabilitiesLength, fetchNextPage, isLoading, hasRequiredData])

  useEffect(() => {
    const allPractitioners = data?.pages.flatMap((page) => page.practitioners) || []
    const allUniquePractitioners = uniqBy(allPractitioners, 'referenceId')
    selectPreferredPractitioners(allUniquePractitioners)
    practitionersDispatch(setAllPractitioners(allPractitioners))
  }, [data?.pages, selectPreferredPractitioners, practitionersDispatch])

  const requestInProgress = isLoading || isFetchingNextPage
  useEffect(() => {
    practitionersDispatch(setPractitionersLoading(requestInProgress))
  }, [requestInProgress, practitionersDispatch])
}
